import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import { formatDate, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Brain, FileText, CurrencyDollar, CloudArrowUp, PaperPlaneTilt,
  Video, CircleNotch, CheckCircle, Clock,
} from '@phosphor-icons/react';
import { BHClaimModal } from '../components/bh/BHClaimModal';
import { StatsCardGrid } from '../components/shared/StatsCardGrid';

const BHBillingPage = () => {
  const [claims, setClaims] = useState([]);
  const [patients, setPatients] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [bhCodes, setBhCodes] = useState({});
  const [bhConfig, setBhConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // ICD-10 handled by ICD10Search component

  const [formData, setFormData] = useState({
    patient_id: '',
    practitioner_id: '',
    service_date: new Date().toISOString().split('T')[0],
    service_code: '',
    units: 1,
    diagnosis_code: '',
    diagnosis_description: '',
    secondary_diagnosis: '',
    place_of_service: '11',
    is_telehealth: false,
    prior_auth_number: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [claimsRes, patientsRes, employeesRes, codesRes, configRes] = await Promise.all([
        api.get('/bh/claims', { params: { status: filterStatus || undefined, category: filterCategory || undefined } }),
        api.get('/patients'),
        api.get('/employees'),
        api.get('/bh/codes'),
        api.get('/bh/config'),
      ]);
      setClaims(claimsRes.data.claims || []);
      setPatients(patientsRes.data || []);
      setPractitioners(employeesRes.data || []);
      setBhCodes(codesRes.data.codes || {});
      setBhConfig(configRes.data || {});
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Update place of service for telehealth
    if (name === 'is_telehealth') {
      setFormData((prev) => ({ 
        ...prev, 
        [name]: value,
        place_of_service: value ? '02' : '11'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/bh/claims', {
        patient_id: formData.patient_id,
        practitioner_id: formData.practitioner_id,
        service_date: formData.service_date,
        service_code: formData.service_code,
        units: parseFloat(formData.units),
        diagnosis_code: formData.diagnosis_code,
        secondary_diagnosis: formData.secondary_diagnosis || null,
        place_of_service: formData.place_of_service,
        is_telehealth: formData.is_telehealth,
        prior_auth_number: formData.prior_auth_number || null,
        notes: formData.notes || null,
      });
      toast.success('BH claim created successfully');
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create claim');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitClaim = async (claimId) => {
    try {
      const response = await api.post(`/bh/claims/${claimId}/submit`);
      if (response.data.success) {
        toast.success('Claim submitted successfully');
        fetchData();
      } else {
        toast.error(response.data.errors?.join(', ') || 'Submit failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    }
  };

  const handleExport837P = async () => {
    setExporting(true);
    try {
      const response = await api.get('/bh/claims/export-837p', {
        params: { status: 'Pending' },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bh_837p_${new Date().toISOString().split('T')[0]}.edi`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('837P file exported successfully');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No pending claims to export');
      } else {
        toast.error('Export failed');
      }
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      practitioner_id: '',
      service_date: new Date().toISOString().split('T')[0],
      service_code: '',
      units: 1,
      diagnosis_code: '',
      diagnosis_description: '',
      secondary_diagnosis: '',
      place_of_service: '11',
      is_telehealth: false,
      prior_auth_number: '',
      notes: '',
    });
    setSelectedCategory('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'bg-slate-100 text-slate-700';
      case 'Pending': return 'bg-amber-100 text-amber-700';
      case 'Submitted': return 'bg-blue-100 text-blue-700';
      case 'Paid': return 'bg-emerald-100 text-emerald-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'MH': 'bg-indigo-100 text-indigo-700',
      'SUD': 'bg-purple-100 text-purple-700',
      'CRISIS': 'bg-red-100 text-red-700',
      'PSR': 'bg-teal-100 text-teal-700',
      'ACT': 'bg-orange-100 text-orange-700',
      'CM': 'bg-cyan-100 text-cyan-700',
      'IHBT': 'bg-pink-100 text-pink-700',
      'IOP': 'bg-violet-100 text-violet-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  // Filter codes by selected category
  const filteredCodes = selectedCategory 
    ? Object.entries(bhCodes).filter(([_, info]) => info.category === selectedCategory)
    : Object.entries(bhCodes);

  // Calculate summary stats
  const summary = claims.reduce((acc, claim) => {
    acc.total += claim.total_amount || 0;
    acc.byStatus[claim.status] = (acc.byStatus[claim.status] || 0) + 1;
    return acc;
  }, { total: 0, byStatus: {} });

  const statsCards = useMemo(
    () => [
      { label: 'Total Claims', value: claims.length, icon: Brain, color: 'indigo', filter: '', testid: 'bh-stat-total' },
      { label: 'Draft', value: summary.byStatus['Draft'] || 0, icon: FileText, color: 'slate', filter: 'Draft', testid: 'bh-stat-draft' },
      { label: 'Pending', value: summary.byStatus['Pending'] || 0, icon: Clock, color: 'amber', filter: 'Pending', testid: 'bh-stat-pending' },
      { label: 'Paid', value: summary.byStatus['Paid'] || 0, icon: CheckCircle, color: 'emerald', filter: 'Paid', testid: 'bh-stat-paid' },
      { label: 'Total Revenue', value: summary.total, icon: CurrencyDollar, color: 'green', isCurrency: true, testid: 'bh-stat-revenue' },
    ],
    [claims.length, summary.byStatus, summary.total]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bh-billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Behavioral Health Billing
          </h1>
          <p className="text-slate-500 mt-1">
            OhioMHAS - Provider Type {bhConfig.provider_type} ({bhConfig.provider_type_name})
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleExport837P}
            disabled={exporting || summary.byStatus['Pending'] === 0}
            data-testid="export-bh-837p-btn"
          >
            {exporting ? (
              <CircleNotch size={18} className="mr-2 animate-spin" />
            ) : (
              <CloudArrowUp size={18} className="mr-2" />
            )}
            Export 837P
          </Button>
          <Button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-900 hover:bg-indigo-800"
            data-testid="new-bh-claim-btn"
          >
            <Plus size={18} className="mr-2" />
            New BH Claim
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCardGrid
        columns={5}
        activeFilter={filterStatus}
        onFilterChange={setFilterStatus}
        cards={statsCards}
      />

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Label className="text-xs text-slate-500 mb-1">Status</Label>
              <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="bh-filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Submitted">Submitted</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs text-slate-500 mb-1">Category</Label>
              <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="bh-filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(bhConfig.categories || {}).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Claims Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>BH Claims</CardTitle>
        </CardHeader>
        <CardContent>
          {claims.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Brain size={48} className="mx-auto mb-4 opacity-50" />
              <p>No behavioral health claims found</p>
              <Button variant="link" onClick={() => setIsModalOpen(true)}>
                Create your first BH claim
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Claim #</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Practitioner</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.map((claim) => (
                  <TableRow key={claim.id}>
                    <TableCell className="font-mono text-sm">{claim.claim_number}</TableCell>
                    <TableCell>
                      <div className="font-medium">{claim.patient_name}</div>
                      <div className="text-xs text-slate-500">{claim.diagnosis_code}</div>
                    </TableCell>
                    <TableCell>
                      <div>{claim.practitioner_name}</div>
                      <div className="text-xs text-slate-500">{claim.practitioner_type}</div>
                    </TableCell>
                    <TableCell>{formatDate(claim.service_date)}</TableCell>
                    <TableCell>
                      <div className="font-mono text-sm">{claim.service_code}</div>
                      <div className="text-xs text-slate-500 max-w-48 truncate">{claim.service_description}</div>
                      {claim.is_telehealth && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          <Video size={10} className="mr-1" />
                          Telehealth
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getCategoryColor(claim.service_category)}>
                        {claim.service_category}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">{formatCurrency(claim.total_amount)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(claim.status)}>{claim.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {claim.status === 'Draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSubmitClaim(claim.id)}
                          data-testid={`submit-bh-claim-${claim.id}`}
                        >
                          <PaperPlaneTilt size={14} className="mr-1" />
                          Submit
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Claim Modal */}
      <BHClaimModal
        open={isModalOpen}
        onOpenChange={(open) => { setIsModalOpen(open); if (!open) resetForm(); }}
        formData={formData}
        setFormData={setFormData}
        onChange={handleChange}
        onSubmit={handleSubmit}
        saving={saving}
        patients={patients}
        practitioners={practitioners}
        bhCodes={bhCodes}
        bhConfig={bhConfig}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        filteredCodes={filteredCodes}
      />
    </div>
  );
};

export default BHBillingPage;
